import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';
import { Search, Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Building2, FilePlus } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children }) {
  const { t, lang, setLang } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [footerPages, setFooterPages] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => setUser(data))
        .catch(() => { localStorage.removeItem('auth_token'); setUser(null); });
    }
  }, [location.pathname]);

  // Load footer pages
  useEffect(() => {
    fetch(`${API}/pages`)
      .then(r => r.json())
      .then(setFooterPages)
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    const token = localStorage.getItem('auth_token');
    fetch(`${API}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    localStorage.removeItem('auth_token');
    setUser(null);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_8c0c0cf6-a334-4d56-b216-30701ed36848/artifacts/piyuaozg_Top-logo-icon-roh-enhance-700h-2.png"
                alt="verstorben.ch Logo"
                fetchpriority="high"
                className="h-8 w-auto"
              />
              <span className="font-heading font-medium text-lg text-[#1E293B] tracking-tight">
                verstorben<span className="text-[#C5A059]">.ch</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/suche"
                className={`text-sm font-medium transition-colors ${isActive('/suche') ? 'text-[#1E293B]' : 'text-[#475569] hover:text-[#1E293B]'}`}
                data-testid="nav-search"
              >
                {t('nav.search')}
              </Link>
              <Link
                to="/persoenlichkeiten"
                className={`text-sm font-medium transition-colors ${isActive('/persoenlichkeiten') ? 'text-[#1E293B]' : 'text-[#475569] hover:text-[#1E293B]'}`}
                data-testid="nav-vip"
              >
                Persönlichkeiten
              </Link>
              <Link
                to="/ereignisse"
                className={`text-sm font-medium transition-colors ${isActive('/ereignisse') ? 'text-[#1E293B]' : 'text-[#475569] hover:text-[#1E293B]'}`}
                data-testid="nav-ereignisse"
              >
                Ereignisse
              </Link>
              <Link
                to="/verzeichnis"
                className={`text-sm font-medium transition-colors ${isActive('/verzeichnis') ? 'text-[#1E293B]' : 'text-[#475569] hover:text-[#1E293B]'}`}
                data-testid="nav-directory"
              >
                {t('nav.directory')}
              </Link>
              <Link
                to="/trauerspruch-generator"
                className={`text-sm font-medium transition-colors ${isActive('/trauerspruch-generator') ? 'text-[#1E293B]' : 'text-[#475569] hover:text-[#1E293B]'}`}
              >
                {t('nav.trauerspruch')}
              </Link>

              {/* Language Switcher */}
              <div className="flex items-center border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="language-switcher">
                {['de', 'fr', 'it'].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                      lang === l
                        ? 'bg-[#1E293B] text-white'
                        : 'text-[#94A3B8] hover:text-[#1E293B] hover:bg-[#F1F5F9]'
                    }`}
                    data-testid={`lang-${l}`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Auth */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-sm text-[#475569]" data-testid="user-menu-trigger">
                      <User className="w-4 h-4" />
                      <span className="max-w-[120px] truncate">{user.name || user.email}</span>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/todesanzeige-erfassen')} data-testid="nav-create-inserat">
                      <FilePlus className="w-4 h-4 mr-2" />
                      {t('nav.create_inserat')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/mein-verzeichnis')} data-testid="nav-my-directory">
                      <Building2 className="w-4 h-4 mr-2" />
                      Mein Verzeichnis
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                        <User className="w-4 h-4 mr-2" />
                        {t('nav.admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/todesanzeige-erfassen" data-testid="nav-create-inserat">
                    <Button variant="outline" className="text-sm px-4 py-2 rounded-sm gap-1.5 hidden lg:inline-flex">
                      <FilePlus className="w-4 h-4" /> {t('nav.create_inserat')}
                    </Button>
                  </Link>
                  <Link to="/login" data-testid="nav-login-button">
                    <Button className="bg-[#1E293B] hover:bg-[#334155] text-white text-sm px-6 py-2 rounded-sm">
                      {t('nav.login')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-[#475569]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[#F1F5F9] bg-white px-4 py-4 space-y-3" data-testid="mobile-menu">
            <Link to="/suche" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>{t('nav.search')}</Link>
            <Link to="/persoenlichkeiten" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>Persönlichkeiten</Link>
            <Link to="/ereignisse" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>Ereignisse</Link>
            <Link to="/verzeichnis" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>{t('nav.directory')}</Link>
            <Link to="/trauerspruch-generator" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>{t('nav.trauerspruch')}</Link>
            <div className="flex gap-1 py-2">
              {['de', 'fr', 'it'].map(l => (
                <button key={l} onClick={() => { setLang(l); setMobileOpen(false); }}
                  className={`px-3 py-1 text-xs rounded-sm ${lang === l ? 'bg-[#1E293B] text-white' : 'text-[#94A3B8]'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <Link to="/todesanzeige-erfassen" className="block" onClick={() => setMobileOpen(false)}>
              <Button variant="outline" className="w-full text-sm rounded-sm gap-1.5">
                <FilePlus className="w-4 h-4" /> {t('nav.create_inserat')}
              </Button>
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>{t('nav.dashboard')}</Link>
                <Link to="/mein-verzeichnis" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>Mein Verzeichnis</Link>
                {user.role === 'admin' && (
                  <Link to="/admin" className="block text-sm text-[#475569]" onClick={() => setMobileOpen(false)}>{t('nav.admin')}</Link>
                )}
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block text-sm text-[#475569]">{t('nav.logout')}</button>
              </>
            ) : (
              <Link to="/login" className="block" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-[#1E293B] text-white text-sm rounded-sm">{t('nav.login')}</Button>
              </Link>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] bg-white mt-auto" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_8c0c0cf6-a334-4d56-b216-30701ed36848/artifacts/piyuaozg_Top-logo-icon-roh-enhance-700h-2.png"
                  alt="verstorben.ch Logo"
                  loading="lazy"
                  className="h-10 w-auto"
                />
                <span className="font-heading font-medium text-[#1E293B] text-lg">
                  verstorben<span className="text-[#C5A059]">.ch</span>
                </span>
              </Link>
              <p className="text-sm text-[#475569] mt-3 max-w-sm leading-relaxed">
                {t('footer.copyright')}
              </p>
              <div className="gold-accent-line mt-4"></div>
            </div>
            <div>
              <ul className="space-y-2 text-sm text-[#475569]">
                <li><Link to="/suche" className="hover:text-[#1E293B] transition-colors">{t('nav.search')}</Link></li>
                <li><Link to="/persoenlichkeiten" className="hover:text-[#1E293B] transition-colors">Persönlichkeiten</Link></li>
                <li><Link to="/ereignisse" className="hover:text-[#1E293B] transition-colors">Ereignisse</Link></li>
                <li><Link to="/verzeichnis" className="hover:text-[#1E293B] transition-colors">{t('nav.directory')}</Link></li>
                <li><Link to="/trauerspruch-generator" className="hover:text-[#1E293B] transition-colors">{t('nav.trauerspruch_footer')}</Link></li>
              </ul>
            </div>
            <div>
              <ul className="space-y-2 text-sm text-[#475569]" data-testid="footer-pages">
                {footerPages.length > 0 ? (
                  footerPages.map(p => {
                    const pathPrefix = lang === 'fr' ? '/page' : lang === 'it' ? '/pagina' : '/seite';
                    const title = p[`title_${lang}`] || p.title_de;
                    return (
                      <li key={p.slug}>
                        <Link to={`${pathPrefix}/${p.slug}`} className="hover:text-[#1E293B] transition-colors">
                          {title}
                        </Link>
                      </li>
                    );
                  })
                ) : (
                  <>
                    <li><span className="text-[#CBD5E1]">{t('footer.privacy')}</span></li>
                    <li><span className="text-[#CBD5E1]">{t('footer.terms')}</span></li>
                    <li><span className="text-[#CBD5E1]">{t('footer.contact')}</span></li>
                  </>
                )}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#F1F5F9] flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-[#94A3B8]">
              &copy;{new Date().getFullYear()} verstorben.ch. Maxxxum AG. Alle Rechte vorbehalten. Made with <span className="text-red-500">♥</span> by Mille. <span className="text-[#CBD5E1]">V-20260506.2</span>
            </span>
            <Link
              to={lang === 'fr' ? '/don' : lang === 'it' ? '/donazione' : '/spenden'}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] hover:bg-[#FDE68A] text-[#92400E] text-xs font-medium rounded-full transition-colors"
              data-testid="donate-footer-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                <line x1="6" y1="1" x2="6" y2="4"></line>
                <line x1="10" y1="1" x2="10" y2="4"></line>
                <line x1="14" y1="1" x2="14" y2="4"></line>
              </svg>
              {t('footer.donate_coffee')}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
